<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="700px"
    :title="$t('order.invoiceDetail')"
    custom-class="invoice-detail-dialog"
    append-to-body
  >
    <div class="invoice-detail-content" v-loading="loading">
      <div class="detail-section">
        <div class="detail-item">
          <div class="label">{{ $t('order.invoiceHeader') }}：</div>
          <div class="value">{{ detailData.title || '-' }}</div>
        </div>
        
        <div class="detail-item">
          <div class="label">{{ $t('order.headerType') }}：</div>
          <div class="value">
            {{ detailData.type == 1 ? $t('order.personal') : $t('order.company') }}
          </div>
        </div>

        <div class="detail-item" v-if="detailData.type == 2">
          <div class="label">{{ $t('order.taxNumber') }}：</div>
          <div class="value">{{ detailData.tax_number || '-' }}</div>
        </div>
        
        <div class="detail-item">
          <div class="label">{{ $t('order.invoiceAmount') }}：</div>
          <div class="value amount">¥{{ detailData.amount || '0.00' }}</div>
        </div>
        
        <div class="detail-item">
          <div class="label">{{ $t('order.invoiceStatus') }}：</div>
          <div class="value">
            <span :class="['status-tag', getStatusClass(detailData.status)]">
              {{ getStatusText(detailData.status) }}
            </span>
          </div>
        </div>
        
        <div class="detail-item">
          <div class="label">{{ $t('order.applyTime') }}：</div>
          <div class="value">{{ detailData.apply_time || '-' }}</div>
        </div>
        
        <div class="detail-item">
          <div class="label">{{ $t('order.email') }}：</div>
          <div class="value">{{ detailData.email || '-' }}</div>
        </div>
        
        <div class="detail-item full-width" v-if="detailData.img">
          <div class="label">{{ $t('order.invoiceImage') }}：</div>
          <div class="value">
            <div class="invoice-image-wrapper" v-if="isImageFile(detailData.img)">
              <img 
                :src="detailData.img" 
                alt="发票图片" 
                class="invoice-image"
                @click="previewImage"
              >
              <div class="image-tip">{{ $t('order.clickToPreview') }}</div>
            </div>
            <div class="invoice-pdf-wrapper" v-else-if="isPdfFile(detailData.img)">
              <div class="pdf-file-info" @click="openPdfInBrowser(detailData.img)">
                <i class="el-icon-document"></i>
                <span class="pdf-filename">{{ getPdfFileName(detailData.img) }}</span>
                <!-- <span class="pdf-tip">{{ $t('order.clickToOpen') }}</span> -->
              </div>
            </div>
            <div v-else class="unknown-file">
              <a :href="detailData.img" target="_blank" class="file-link">
                {{ detailData.img }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">{{ $t('order.close') }}</el-button>
    </div>

    <!-- 图片预览 -->
    <el-dialog
      :visible.sync="imagePreviewVisible"
      width="80%"
      append-to-body
      custom-class="image-preview-dialog"
    >
      <img :src="detailData.img" alt="发票预览" class="preview-image">
    </el-dialog>
  </el-dialog>
</template>

<script>
export default {
  name: "InvoiceDetailModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    invoiceId: {
      type: [String, Number],
      default: ''
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit("update:visible", val)
      }
    }
  },
  data() {
    return {
      loading: false,
      imagePreviewVisible: false,
      detailData: {}
    }
  },
  watch: {
    visible(val) {
      if (val && this.invoiceId) {
        this.getInvoiceDetail()
      }
    }
  },
  methods: {
    handleClose() {
      this.dialogVisible = false
    },
    previewImage() {
      this.imagePreviewVisible = true
    },
    isImageFile(url) {
      if (!url) return false
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']
      const lowerUrl = url.toLowerCase()
      return imageExtensions.some(ext => lowerUrl.includes(ext))
    },
    isPdfFile(url) {
      if (!url) return false
      return url.toLowerCase().includes('.pdf')
    },
    getPdfFileName(url) {
      if (!url) return 'invoice.pdf'
      const parts = url.split('/')
      const filename = parts[parts.length - 1]
      return decodeURIComponent(filename)
    },
    openPdfInBrowser(url) {
      window.open(url, '_blank')
    },
    getStatusClass(status) {
      const statusMap = {
        0: 'pending',
        1: 'approved',
        2: 'rejected'
      }
      return statusMap[status] || 'pending'
    },
    getStatusText(status) {
      const statusMap = {
        0: this.$t('page.users.invoiceList.status[0]'),
        1: this.$t('page.users.invoiceList.status[1]'),
        2: this.$t('page.users.invoiceList.status[2]')
      }
      return statusMap[status] || '-'
    },
    getInvoiceDetail() {
      this.loading = true
      this.$axios.post('/api/center/getInvoiceDetail', {
        id: this.invoiceId
      }).then(res => {
        this.detailData = res.data || {}
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .invoice-detail-dialog {
  border-radius: 4px;
  
  .el-dialog__header {
    padding: 20px;
    border-bottom: 1px solid #eee;
  }
  
  .el-dialog__body {
    padding: 30px 40px;
    max-height: 70vh;
    overflow-y: auto;
  }
  
  .el-dialog__footer {
    padding: 15px 20px;
    border-top: 1px solid #eee;
    text-align: center;
  }
}

::v-deep .image-preview-dialog {
  .el-dialog__body {
    padding: 0;
    text-align: center;
    background: #000;
  }
}

.invoice-detail-content {
  .detail-section {
    .detail-item {
      display: flex;
      margin-bottom: 20px;
      align-items: flex-start;
      
      &.full-width {
        flex-direction: column;
      }
      
      .label {
        width: 120px;
        color: #666;
        font-size: 14px;
        flex-shrink: 0;
      }
      
      .value {
        flex: 1;
        color: #333;
        font-size: 14px;
        word-break: break-all;
        
        &.amount {
          color: #e93323;
          font-size: 18px;
          font-weight: bold;
        }
      }
      
      .status-tag {
        display: inline-block;
        padding: 4px 12px;
        border-radius: 4px;
        font-size: 12px;
        
        &.pending {
          background: #fff7e6;
          color: #faad14;
          border: 1px solid #ffd591;
        }
        
        &.approved {
          background: #f6ffed;
          color: #52c41a;
          border: 1px solid #b7eb8f;
        }
        
        &.rejected {
          background: #fff2f0;
          color: #ff4d4f;
          border: 1px solid #ffccc7;
        }
      }
      
      .invoice-image-wrapper {
        .invoice-image {
          max-width: 100%;
          height: auto;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.3s;
          border: 1px solid #eee;
          
          &:hover {
            box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
            transform: scale(1.02);
          }
        }
        
        .image-tip {
          margin-top: 8px;
          font-size: 12px;
          color: #999;
          text-align: center;
        }
      }
      
      .invoice-pdf-wrapper {
        .pdf-file-info {
          display: flex;
          align-items: center;
          padding: 16px 20px;
          background: #f7f8fa;
          border: 1px solid #e4e7ed;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.3s;
          
          &:hover {
            background: #ecf5ff;
            border-color: #b3d8ff;
            
            .pdf-filename {
              color: #409eff;
            }
          }
          
          .el-icon-document {
            font-size: 24px;
            color: #e93323;
            margin-right: 12px;
          }
          
          .pdf-filename {
            flex: 1;
            font-size: 14px;
            color: #333;
            word-break: break-all;
            transition: color 0.3s;
          }
          
          .pdf-tip {
            font-size: 12px;
            color: #999;
            margin-left: 12px;
            white-space: nowrap;
          }
        }
      }
      
      .unknown-file {
        .file-link {
          color: #409eff;
          text-decoration: none;
          word-break: break-all;
          
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
}

.preview-image {
  max-width: 100%;
  max-height: 80vh;
  display: block;
  margin: 0 auto;
}
</style>
